import React, { useMemo, useState } from 'react';
import { Users, GraduationCap, Heart, Shield, Plus, Eye, Edit2, PowerOff, Link as LinkIcon, School, Search, Filter, ChevronDown } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { DataTable } from '../shared/DataTable';
import { SlideOver } from '../../shared/SlideOver';
import { AdminTabs, AdminTab } from '../shared/AdminTabs';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { isLearnerAccount } from '../../../accounts';
import { useUsersData } from './users/useUsersData';
import { getColumnsForRole, schoolColumns } from './users/columns';
import { UserDetailPanel } from './users/UserDetailPanel';
import { SchoolDetailPanel } from './users/SchoolDetailPanel';
import { AddUserForm } from './users/AddUserForm';
import { LinkUsersForm } from './users/LinkUsersForm';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { ROLE_CONFIG } from './users/types';
import type { UsersPageProps, RoleFilter, UserRow, SchoolData } from './users/types';
export function UsersPage({
  accounts: propsAccounts,
  getLearnerData: propsGetLearnerData,
  getLearnerModuleMastery,
  onNavigateToLearner
}: UsersPageProps) {
  const store = useAppStore();
  const accounts = propsAccounts || store.accounts;
  const getLearnerData = propsGetLearnerData || store.getLearnerData;
  // View state
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [selectedSchool, setSelectedSchool] = useState<SchoolData | null>(null);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<UserRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
  // Data hooks
  const {
    users,
    allSchools,
    counts,
    filtered,
    filteredSchools,
    selectedMastery
  } = useUsersData({
    accounts,
    getLearnerData,
    roleFilter,
    searchQuery,
    schoolFilter,
    selectedUser
  });
  // Build tabs with counts
  const roleTabs: AdminTab[] = useMemo(() => [{
    id: 'all',
    label: `Alle (${counts.all})`,
    icon: <Users className="w-4 h-4" data-id="element-6791" />
  }, {
    id: 'learner',
    label: `Leerlingen (${counts.learner})`,
    icon: <Users className="w-4 h-4" data-id="element-6792" />
  }, {
    id: 'teacher',
    label: `Docenten (${counts.teacher})`,
    icon: <GraduationCap className="w-4 h-4" data-id="element-6793" />
  }, {
    id: 'school',
    label: `Scholen (${counts.school})`,
    icon: <School className="w-4 h-4" data-id="element-6794" />
  }, {
    id: 'parent',
    label: `Ouders (${counts.parent})`,
    icon: <Heart className="w-4 h-4" data-id="element-6795" />
  }, {
    id: 'admin',
    label: `Admins (${counts.admin})`,
    icon: <Shield className="w-4 h-4" data-id="element-6796" />
  }], [counts]);
  const openEditMode = (row: UserRow) => {
    setSelectedUser(row);
    setIsEditing(true);
  };
  const rowActions = [{
    label: 'Bekijken',
    icon: Eye,
    onClick: (row: UserRow) => {
      setSelectedUser(row);
      setIsEditing(false);
    }
  }, {
    label: 'Bewerken',
    icon: Edit2,
    onClick: openEditMode
  }, ...(onNavigateToLearner ? [{
    label: 'Open dashboard',
    icon: Eye,
    onClick: (row: UserRow) => row.role === 'learner' && onNavigateToLearner(row.id)
  }] : []), {
    label: 'Verwijderen',
    icon: PowerOff,
    onClick: (row: UserRow) => setDeleteConfirmUser(row),
    danger: true
  }];
  return <PageLayout title="Gebruikers" subtitle="Alle accounts — leerlingen, docenten, ouders en admins" breadcrumbs={[{
    label: 'Gebruikers'
  }, {
    label: 'Overzicht'
  }]} actions={<div className="flex gap-2" data-id="element-6798">
          <ConnectionBadge status="live" data-id="element-6799" />
          <Button size="sm" variant="secondary" onClick={() => setIsLinkModalOpen(true)} data-id="element-6800">
            <LinkIcon className="w-4 h-4 mr-1.5" data-id="element-6801" />
            Koppelen
          </Button>
          <Button size="sm" onClick={() => setIsAddModalOpen(true)} data-id="element-6802">
            <Plus className="w-4 h-4 mr-1.5" data-id="element-6803" />
            Toevoegen
          </Button>
        </div>} data-id="element-6797">
      {/* Role filter tabs */}
      <AdminTabs tabs={roleTabs} activeTab={roleFilter} onTabChange={id => {
      setRoleFilter(id as RoleFilter);
      setSearchQuery('');
      setSchoolFilter('');
      setSelectedIds([]);
    }} data-id="element-6804" />

      {/* Bulk Actions Toolbar */}
      {selectedIds.length > 0 && <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4 flex items-center justify-between shadow-sm" data-id="element-6805">
          <div className="flex items-center gap-2 text-sm font-medium text-blue-900" data-id="element-6806">
            <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full" data-id="element-6807">
              {selectedIds.length}
            </span>
            geselecteerd
          </div>
          <div className="flex items-center gap-2" data-id="element-6808">
            <Button size="sm" variant="secondary" onClick={() => setSelectedIds([])} className="bg-white" data-id="element-6809">
              Deselecteren
            </Button>
            <Button size="sm" variant="danger" onClick={() => setIsBulkDeleteConfirmOpen(true)} data-id="element-6810">
              <PowerOff className="w-4 h-4 mr-1.5" data-id="element-6811" />
              Verwijderen
            </Button>
          </div>
        </div>}

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4" data-id="element-6812">
        <div className="relative flex-1 max-w-sm" data-id="element-6813">
          <Input type="text" placeholder={roleFilter === 'school' ? 'Zoek op schoolnaam...' : 'Zoek op naam of e-mail...'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-6815" />} className="pl-9" data-id="element-6814" />
        </div>
        {roleFilter === 'learner' && allSchools.length > 0 && <div className="relative" data-id="element-6816">
            <Select value={schoolFilter} onChange={e => setSchoolFilter(e.target.value)} icon={<Filter className="w-4 h-4" data-id="element-6818" />} options={allSchools.map(s => ({
          value: s,
          label: s
        }))} placeholder="Alle scholen" className="pl-9" fullWidth={false} data-id="element-6817" />
          </div>}
      </div>

      {/* Table */}
      {roleFilter === 'school' ? filteredSchools.length === 0 ? <div className="bg-white rounded-xl border border-gray-200 py-16 text-center shadow-sm" data-id="element-6819">
            <School className="w-10 h-10 text-gray-200 mx-auto mb-3" data-id="element-6820" />
            <p className="text-sm text-gray-500" data-id="element-6821">Geen scholen gevonden</p>
          </div> : <DataTable columns={schoolColumns} data={filteredSchools} onRowClick={row => setSelectedSchool(row)} data-id="element-6822" /> : filtered.length === 0 ? <div className="bg-white rounded-xl border border-gray-200 py-16 text-center shadow-sm" data-id="element-6823">
          <Users className="w-10 h-10 text-gray-200 mx-auto mb-3" data-id="element-6824" />
          <p className="text-sm text-gray-500" data-id="element-6825">Geen gebruikers gevonden</p>
          {(searchQuery || schoolFilter) && <button onClick={() => {
        setSearchQuery('');
        setSchoolFilter('');
      }} className="mt-2 text-xs text-blue-600 hover:underline" data-id="element-6826">
              Filters wissen
            </button>}
        </div> : <DataTable columns={getColumnsForRole(roleFilter)} data={filtered} onRowClick={row => {
      setSelectedUser(row);
      setIsEditing(false);
    }} rowActions={roleFilter !== 'school' ? rowActions : undefined} searchable={false} pagination={true} pageSize={25} selectable={roleFilter !== 'school'} selectedIds={selectedIds} onSelectionChange={setSelectedIds} data-id="element-6827" />}

      {/* User Detail SlideOver */}
      <SlideOver open={!!selectedUser} onClose={() => {
      setSelectedUser(null);
      setIsEditing(false);
    }} title={isEditing ? 'Gebruiker bewerken' : selectedUser?.name || ''} description={selectedUser && !isEditing ? ROLE_CONFIG[selectedUser.role].label : undefined} data-id="element-6828">
        {selectedUser && <UserDetailPanel user={selectedUser} isEditing={isEditing} selectedMastery={selectedMastery} accounts={accounts} store={store} onNavigateToLearner={onNavigateToLearner} onClose={() => setSelectedUser(null)} onStartEdit={openEditMode} onStopEdit={() => setIsEditing(false)} onUserUpdate={setSelectedUser} data-id="element-6829" />}
      </SlideOver>

      {/* School Detail SlideOver */}
      <SlideOver open={!!selectedSchool} onClose={() => setSelectedSchool(null)} title={selectedSchool?.name ?? ''} data-id="element-6830">
        {selectedSchool && <SchoolDetailPanel school={selectedSchool} data-id="element-6831" />}
      </SlideOver>

      {/* Add User Modal */}
      <SlideOver open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Nieuwe gebruiker" description="Maak een nieuw account aan" data-id="element-6832">
        <AddUserForm accounts={accounts} store={store} onClose={() => setIsAddModalOpen(false)} data-id="element-6833" />
      </SlideOver>

      {/* Link Users Modal */}
      <SlideOver open={isLinkModalOpen} onClose={() => setIsLinkModalOpen(false)} title="Accounts koppelen" description="Koppel een ouder of docent aan een leerling" data-id="element-6834">
        <LinkUsersForm users={users} accounts={accounts} store={store} onClose={() => setIsLinkModalOpen(false)} data-id="element-6835" />
      </SlideOver>

      <ConfirmDialog open={deleteConfirmUser !== null} title="Account verwijderen" message={deleteConfirmUser ? `Weet je zeker dat je ${deleteConfirmUser.name} wilt verwijderen? Dit kan niet ongedaan worden gemaakt.` : ''} confirmLabel="Verwijderen" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (deleteConfirmUser) {
        store.deleteAccount(deleteConfirmUser.id);
      }
      setDeleteConfirmUser(null);
    }} onCancel={() => setDeleteConfirmUser(null)} data-id="element-6836" />

      <ConfirmDialog open={isBulkDeleteConfirmOpen} title="Meerdere accounts verwijderen" message={`Weet je zeker dat je ${selectedIds.length} accounts wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`} confirmLabel="Verwijderen" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      selectedIds.forEach(id => store.deleteAccount(id));
      setSelectedIds([]);
      setIsBulkDeleteConfirmOpen(false);
    }} onCancel={() => setIsBulkDeleteConfirmOpen(false)} data-id="element-6837" />
    </PageLayout>;
}